import React, { useMemo } from 'react';
import './RegulatoryBodyReport.scss';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useGridApiContext } from '@mui/x-data-grid';
import moment from 'moment';

import DeviceLink from 'src/component/UI/DeviceLink';
import { getClient } from 'src/module/authentication/selector';
import { generateTestingReportPdf } from 'src/module/device/action';
import { useTranslation } from 'src/i18n';
import { useReportData } from 'src/hooks/useReportData';
import { useReportColumns } from 'src/hooks/useReportColumns';
import { formatTemperature, formatLoadCurrent, getFieldLabel as getFieldLabelUtil } from 'src/utils/reportUtils';
import BaseReport from 'src/component/Report/BaseReport';
import ReportToolbar from 'src/component/Report/ReportToolbar';

/**
 * Creates a device link column for controller or circuit tags
 */
const createDeviceLinkColumn = (
  field: string,
  headerName: string,
  isController: boolean = false
) => ({
  field,
  headerName,
  minWidth: 100,
  renderCell: (params: any) => {
    if (params.value === null || params.value === undefined || params.value === '') {
      return '-';
    }
    if (isController) {
      return <DeviceLink label={params.value} controllerTag={params.value} />;
    }
    return (
      <DeviceLink
        label={params.value}
        circuitTag={params.value}
        controllerTag={params.row.controller_tag}
      />
    );
  },
  flex: 1
});

/**
 * Creates a formatted column for temperature or current values
 */
const createFormattedColumn = (
  field: string,
  headerName: string,
  formatter: (value: any) => string
) => ({
  field,
  headerName,
  minWidth: 100,
  valueFormatter: (params: any) => formatter(params.value),
  flex: 1
});

/**
 * Creates a default column with generic formatting
 */
const createDefaultColumn = (field: string, headerName: string, minWidth: number = 100) => ({
  field,
  headerName,
  minWidth,
  valueFormatter: (params: any) => {
    if (params.value === null || params.value === undefined || params.value === '') {
      return '-';
    }
    return params.value;
  },
  flex: 1
});

/**
 * Creates the combined controller/circuit column
 */
const createCombinedControllerCircuitColumn = (headerName: string) => ({
  field: 'controller_circuit',
  headerName,
  minWidth: 200,
  renderCell: (params: any) => {
    const controllerTag = params.row.controller_tag;
    const circuitTag = params.row.tag || params.row.circuit_tag;

    // If both tags exist, show combined format
    if (controllerTag && circuitTag) {
      return (
        <div style={{ padding: '4px 0' }}>
          <DeviceLink label={controllerTag} controllerTag={controllerTag} /> /{' '}
          <DeviceLink label={circuitTag} circuitTag={circuitTag} controllerTag={controllerTag} />
        </div>
      );
    }
    // If only controller tag exists
    if (controllerTag && !circuitTag) {
      return (
        <div style={{ padding: '4px 0' }}>
          <DeviceLink label={controllerTag} controllerTag={controllerTag} />
        </div>
      );
    }
    // If only circuit tag exists
    if (!controllerTag && circuitTag) {
      return (
        <div style={{ padding: '4px 0' }}>
          <DeviceLink label={circuitTag} circuitTag={circuitTag} controllerTag={null} />
        </div>
      );
    }
    // Neither exists
    return '-';
  },
  flex: 1
});

/**
 * Gets the list of fields to export, excluding the combined column
 */
const getExportFields = (data: any, columns: any[]): string[] => {
  const exportFields: string[] = [];

  // Add controller_tag first if it exists in data
  if (data && data.size > 0) {
    const firstRow = data.toJS()[0];
    if (firstRow?.controller_tag !== undefined) {
      exportFields.push('controller_tag');
    }

    // Add tag/circuit_tag second if it exists in data
    if (firstRow?.tag !== undefined) {
      exportFields.push('tag');
    } else if (firstRow?.circuit_tag !== undefined) {
      exportFields.push('circuit_tag');
    }
  }

  // Add all other fields (excluding controller_circuit and the tag fields we already added)
  columns.forEach(col => {
    if (
      col.field !== 'controller_circuit' &&
      col.field !== 'controller_tag' &&
      col.field !== 'tag' &&
      col.field !== 'circuit_tag' &&
      !exportFields.includes(col.field)
    ) {
      exportFields.push(col.field);
    }
  });

  return exportFields;
};

export default function MultiCircuitReport({ filters }) {
  const { t } = useTranslation();
  const { data, loading, pdfLoading } = useReportData('multi');
  const client = useSelector(getClient);
  const dispatch = useDispatch();

  // Default field label mappings (can be overridden by client preferences)
  const defaultFieldLabels = useMemo(
    () => ({
      tag: t('multiCircuitReport.circuitTag'),
      controller_tag: t('multiCircuitReport.controllerTag'),
      controller_circuit: t('alarms.deviceTag'), // Use "Device Tag" for the combined column
      line_description: t('regulatoryReport.lineDescription'),
      status_temperatureSensors_1_temperature: t('multiCircuitReport.lastReadRtd1Temperature'),
      status_temperatureSensors_2_temperature: t('multiCircuitReport.lastReadRtd2Temperature'),
      status_loadCurrent: t('multiCircuitReport.lastReadLoadCurrent'),
      current_alarms: t('multiCircuitReport.currentAlarms')
    }),
    [t]
  );

  // Get custom field labels from client preferences, fall back to defaults
  const getFieldLabel = (fieldName: string): string => {
    return getFieldLabelUtil(fieldName, defaultFieldLabels, client);
  };

  // Create column definitions with current labels
  const columnDefinitions = useMemo(() => {
    const getLabel = (fieldName: string) => getFieldLabel(fieldName);

    return {
      // Combined controller/circuit column (similar to AlarmsList)
      controller_circuit: createCombinedControllerCircuitColumn(getLabel('controller_circuit')),
      controller_tag: createDeviceLinkColumn(getLabel('controller_tag'), getLabel('controller_tag'), true),
      tag: createDeviceLinkColumn(getLabel('tag'), getLabel('tag'), false),
      status_temperatureSensors_1_temperature: createFormattedColumn(
        'status_temperatureSensors_1_temperature',
        getLabel('status_temperatureSensors_1_temperature'),
        formatTemperature
      ),
      status_temperatureSensors_2_temperature: createFormattedColumn(
        'status_temperatureSensors_2_temperature',
        getLabel('status_temperatureSensors_2_temperature'),
        formatTemperature
      ),
      status_loadCurrent: createFormattedColumn(
        'status_loadCurrent',
        getLabel('status_loadCurrent'),
        formatLoadCurrent
      ),
      current_alarms: createDefaultColumn('current_alarms', getLabel('current_alarms'), 300)
    };
  }, [getFieldLabel]);

  // Dynamically build columns based on the data returned
  const columns = useReportColumns(
    data,
    columnDefinitions,
    getFieldLabel,
    columnDefinitions.controller_circuit
  );

  const handlePdfGenerationSubmit = () => {
    if (!pdfLoading) {
      dispatch(
        generateTestingReportPdf(
          'multi',
          filters.selectedFilters,
          null,
          filters.startDate ? moment(filters.startDate).format('YYYY-MM-DD') : null,
          filters.endDate ? moment(filters.endDate).format('YYYY-MM-DD') : null
        )
      );
    }
  };

  const CustomToolbar = () => {
    const apiRef = useGridApiContext();

    const handleExport = (options: any) => {
      // Use apiRef from options if provided (from ReportToolbar), otherwise use context
      const gridApiRef = options.apiRef || apiRef;
      if (gridApiRef && gridApiRef.current) {
        const exportFields = getExportFields(data, columns);
        // Export with fields option - headers will come from column definitions (including hidden ones)
        gridApiRef.current.exportDataAsCsv({
          ...options,
          fields: exportFields
        });
      }
    };

    return (
      <ReportToolbar
        onExport={handleExport}
        onPdfDownload={handlePdfGenerationSubmit}
        pdfLoading={pdfLoading}
        showPdf={true}
        exportLabel={t('multiCircuitReport.downloadCsv')}
        pdfLabel={t('multiCircuitReport.downloadPdf')}
      />
    );
  };

  const getRowId = (row: any, index: number): string => {
    // Use controller_tag and tag/circuit_tag if available, otherwise fall back to index
    if (row.controller_tag && (row.tag || row.circuit_tag)) {
      return `${row.controller_tag || ''}-${row.tag || row.circuit_tag || ''}`;
    }
    // Fallback: use tag if available, otherwise use index
    return row.tag || row.circuit_tag || `row-${index}`;
  };

  return (
    <BaseReport
      title={t('multiCircuitReport.title')}
      data={data?.toJS() || []}
      loading={loading}
      columns={columns}
      customToolbar={CustomToolbar}
      getRowId={getRowId}
    />
  );
}

MultiCircuitReport.propTypes = {
  filters: PropTypes.object
};

MultiCircuitReport.defaultProps = {};
